import { Building2, CircleX, Flag, RotateCcw } from "lucide-react";
import moment from "moment";
import * as React from "react";
import { Link } from "react-router-dom";

import { Svg } from "@/assets";
import { Badge, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { User, VideoProject } from "@/services/bedrock";
import { URLS } from "@/utils";

import {
  VideoProjectStaffPicker,
  canUnassignStaff,
  recommendedStaffForRole,
  resolveStaffUser,
} from "@/components/VideoProjectStaffPicker";
import { VideoProjectStatusPicker } from "@/components/VideoProjectStatusPicker";
import { mediaUrlIdentity } from "./kanbanColumnQuery";
import { KanbanColumnId } from "./kanbanColumns";

interface VideoProjectKanbanCardProps {
  videoProject: VideoProject;
  staffUsers: User[];
  currentUserId?: number;
  disabled?: boolean;
  interactive?: boolean;
  selected?: boolean;
  onAssigneeChange?: (project: VideoProject, userId: number | null) => void;
  onQaChange?: (project: VideoProject, userId: number | null) => void;
  onStatusChange?: (project: VideoProject, columnId: KanbanColumnId) => void;
}

function revisionBadgeClass(revisionCount?: number) {
  if (!revisionCount || revisionCount <= 0) return "text-muted-foreground border-border";
  if (revisionCount === 1) return "bg-amber-50 text-amber-800 border-amber-200";
  if (revisionCount === 2) return "bg-orange-50 text-orange-800 border-orange-200";
  return "bg-red-50 text-red-800 border-red-200";
}

const KanbanProjectThumbnail = React.memo(function KanbanProjectThumbnail({ src }: { src?: string | null }) {
  const nextSrc = src || Svg.PromptPlaceholder;
  const displaySrcRef = React.useRef(nextSrc);
  if (mediaUrlIdentity(displaySrcRef.current) !== mediaUrlIdentity(nextSrc)) {
    displaySrcRef.current = nextSrc;
  } else if (!displaySrcRef.current) {
    displaySrcRef.current = nextSrc;
  }

  return (
    <img
      src={displaySrcRef.current}
      alt=""
      className={cn("size-full", src ? "object-cover object-center" : "object-none object-center")}
    />
  );
});

function cardDateLabel(videoProject: VideoProject) {
  if (videoProject.createdDate) {
    return `Created ${moment(videoProject.createdDate).format("MMM D")}`;
  }
  if (videoProject.statusDate) {
    return `Updated ${moment(videoProject.statusDate).format("MMM D")}`;
  }
  return null;
}

export const VideoProjectKanbanCard = React.memo(function VideoProjectKanbanCard({
  videoProject,
  staffUsers,
  currentUserId,
  disabled = false,
  interactive = true,
  selected = false,
  onAssigneeChange,
  onQaChange,
  onStatusChange,
}: VideoProjectKanbanCardProps) {
  const assignments = videoProject.queueAssignments ?? [];
  const assignee = resolveStaffUser(videoProject.editorUserId, staffUsers, assignments);
  const qaUser = resolveStaffUser(videoProject.qaUserId, staffUsers, assignments);
  const recommendedEditors = recommendedStaffForRole(videoProject, "EDITOR", staffUsers);
  const recommendedQa = recommendedStaffForRole(videoProject, "QA", staffUsers);
  const dateLabel = cardDateLabel(videoProject);
  const pickerDisabled = disabled || !interactive;

  return (
    <Card
      size="sm"
      disabled={disabled}
      data-selected={selected ? "" : undefined}
      className={cn(
        "relative gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10 transition-[box-shadow,ring-color,ring-width] duration-200",
        selected ? "z-10 shadow-md ring-2 ring-ring/50" : "hover:shadow-md hover:ring-foreground/20",
        videoProject.isPriority &&
          "before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-[inherit] before:bg-sky-500",
        videoProject.needsAck && (selected ? "ring-2 ring-amber-500" : "ring-amber-500")
      )}
    >
      <Link
        to={URLS.editor.editVideo.replace(":id", `${videoProject.id}`)}
        aria-label={videoProject.playerName || `Video project ${videoProject.id}`}
        className={cn("absolute inset-0 z-0", disabled && "pointer-events-none")}
      />
      <div className="relative z-10 flex flex-col gap-2 p-2.5 pointer-events-none">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground flex min-w-0 items-center gap-1 font-mono text-[11px]">
            {videoProject.isPriority ? (
              <span title="Priority">
                <Flag className="size-3 shrink-0 fill-sky-500 text-sky-500" aria-hidden="true" />
              </span>
            ) : null}
            #{videoProject.id}
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            <VideoProjectStaffPicker
              role="EDITOR"
              compact
              selectedUserId={videoProject.editorUserId}
              selectedUser={assignee}
              otherRoleUserId={videoProject.qaUserId}
              recommendedUsers={recommendedEditors}
              staffUsers={staffUsers}
              currentUserId={currentUserId}
              interactive={interactive}
              disabled={pickerDisabled}
              allowUnassign={canUnassignStaff("EDITOR", videoProject.status)}
              onSelect={userId => onAssigneeChange?.(videoProject, userId)}
            />
            <VideoProjectStaffPicker
              role="QA"
              compact
              selectedUserId={videoProject.qaUserId}
              selectedUser={qaUser}
              otherRoleUserId={videoProject.editorUserId}
              recommendedUsers={recommendedQa}
              staffUsers={staffUsers}
              currentUserId={currentUserId}
              interactive={interactive}
              disabled={pickerDisabled}
              allowUnassign={canUnassignStaff("QA", videoProject.status)}
              onSelect={userId => onQaChange?.(videoProject, userId)}
            />
          </div>
        </div>

        <div className="flex items-start gap-2.5">
          <div className="bg-muted size-16 shrink-0 overflow-hidden rounded-md">
            <KanbanProjectThumbnail src={videoProject.thumbnailUrl} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-start gap-1">
              <VideoProjectStatusPicker
                status={videoProject.status}
                requiresQa={videoProject.requiresQa}
                currentUserId={currentUserId}
                interactive={interactive}
                disabled={pickerDisabled}
                onSelect={columnId => onStatusChange?.(videoProject, columnId)}
              />
              <div className="text-foreground line-clamp-2 min-w-0 pt-0.5 text-left text-sm leading-snug font-medium tracking-tight">
                {videoProject.playerName || "Untitled Player"}
              </div>
            </div>
            {videoProject.companyName || videoProject.qaStatus === "REJECTED" ? (
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                {videoProject.qaStatus === "REJECTED" ? (
                  <Badge
                    variant="outline"
                    className="h-5 gap-1 border-violet-200 bg-violet-50 px-1.5 py-0 text-[10px] font-medium text-violet-800 shadow-none"
                    title="QA failed"
                  >
                    <CircleX className="size-2.5" />
                    QA failed
                  </Badge>
                ) : null}
                {videoProject.companyName ? (
                  <Badge
                    variant="outline"
                    className="text-muted-foreground h-5 max-w-full gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none"
                    title={videoProject.companyName}
                  >
                    <Building2 className="size-2.5 shrink-0 text-orange-500" />
                    <span className="truncate">{videoProject.companyName}</span>
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {dateLabel || (videoProject.revisionCount && videoProject.revisionCount > 0) ? (
          <div className="flex items-center justify-between gap-2">
            {dateLabel ? <div className="text-muted-foreground min-w-0 truncate text-[11px]">{dateLabel}</div> : null}
            {videoProject.revisionCount && videoProject.revisionCount > 0 ? (
              <Badge
                variant="outline"
                className={cn(
                  "ml-auto h-5 shrink-0 gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none",
                  revisionBadgeClass(videoProject.revisionCount)
                )}
                title={`Revisions: ${videoProject.revisionCount}`}
              >
                <RotateCcw className="size-2.5" />
                {videoProject.revisionCount}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
});
